import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { opsApi } from '../services/ops';
import { getIdentityScope } from '../stores/useAuthStore';

// Scoped to the active identity - operational data is admin only and must not
// survive into a session with different privileges
export const opsKeys = {
  all: ['ops'] as const,
  scoped: () => [...opsKeys.all, getIdentityScope()] as const,
  alerts: () => [...opsKeys.scoped(), 'alerts'] as const,
  health: () => [...opsKeys.scoped(), 'health'] as const,
  activity: () => [...opsKeys.scoped(), 'activity'] as const,
};

export function useOpsAlerts() {
  return useQuery({
    queryKey: opsKeys.alerts(),
    queryFn: async () => (await opsApi.getAlerts()).data,
    refetchInterval: 30_000,
  });
}

export function useOpsHealth() {
  return useQuery({
    queryKey: opsKeys.health(),
    queryFn: async () => (await opsApi.getHealth()).data,
    refetchInterval: 30_000,
  });
}

export function useOpsActivity() {
  return useQuery({
    queryKey: opsKeys.activity(),
    queryFn: async () => (await opsApi.getActivity()).data,
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      opsApi.updateAlert(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: opsKeys.all }),
  });
}

export function useRetryAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => opsApi.retryAlert(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: opsKeys.all }),
  });
}
